<script setup>
import { ref, onBeforeUnmount, computed } from "vue";
import axios from "axios";
import { useRouter, onBeforeRouteLeave } from "vue-router";
import AgentLayout from "@/views/layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import { useToast } from "vue-toastification";
import { useCompanyStore } from "@/stores/company";

const toast = useToast();
const router = useRouter();
const company_id = useCompanyStore().companyId;
const headers = {
  Accept: "application/json",
  "Content-type": "multipart/form-data",
};

// Form data
const mediaName = ref("");
const mediaFile = ref(null);
const mediaUrl = ref("");
const mediaType = ref(""); // 'image', 'video', 'document'
const inputMode = ref("file"); // 'file' or 'url'
const fileInputRef = ref(null);
const previewUrl = ref(null);
const fileSize = ref(0);
const formattedSize = computed(() => {
  if (!fileSize.value) return "";
  const sizeInMB = (fileSize.value / (1024 * 1024)).toFixed(2);
  return `${sizeInMB} MB`;
});
const fileDimensions = ref("");
const duration = ref(0);
const formattedDuration = computed(() => {
  if (!duration.value) return "";
  const minutes = Math.floor(duration.value / 60);
  const seconds = duration.value % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});
const isSubmitted = ref(false);
const uploadPercentage = ref(0);
const isUploading = ref(false);

// File type options
const fileTypes = [
  { value: "image", label: "Image", icon: "🖼️" },
  { value: "video", label: "Video", icon: "🎥" },
  //   { value: "document", label: "Document", icon: "📄" },
];

const switchInputMode = (mode) => {
  inputMode.value = mode;
  mediaType.value = "";
  // Clear the other input when switching
  if (mode === "file") {
    mediaUrl.value = "";
  } else {
    removeFile();
  }
  previewUrl.value = null;
};

const handleUrlInput = () => {
  if (!mediaUrl.value) {
    previewUrl.value = null;
    return;
  }

  // Set preview URL for images and videos
  previewUrl.value = mediaUrl.value;

  // Auto-detect media type from URL
  const url = mediaUrl.value.toLowerCase();
  if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) {
    mediaType.value = "image";
  } else if (url.match(/\.(mp4|mov|avi|webm)$/)) {
    mediaType.value = "video";
  } else {
    mediaType.value = "";
  }
  // Extract filename from URL if name is empty
  if (!mediaName.value) {
    const urlParts = mediaUrl.value.split("/");
    const filename = urlParts[urlParts.length - 1];
    mediaName.value = filename || "Media from URL";
  }
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  mediaFile.value = file;
  mediaName.value = file.name;

  // Store file size in bytes
  fileSize.value = file.size;

  // Create preview URL for images and videos
  if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
    previewUrl.value = URL.createObjectURL(file);
  }

  // Get dimensions for images
  if (file.type.startsWith("image/")) {
    const img = new Image();
    img.onload = () => {
      fileDimensions.value = `${img.width}x${img.height}`;
    };
    img.src = previewUrl.value;
  }

  // Get duration for videos
  if (file.type.startsWith("video/")) {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      duration.value = Math.floor(video.duration);
    };
    video.src = previewUrl.value;
  }

  // Detect media type
  if (file.type.startsWith("image/")) {
    mediaType.value = "image";
  } else if (file.type.startsWith("video/")) {
    mediaType.value = "video";
  } else {
    mediaType.value = "document";
  }
};

const triggerFileInput = () => {
  fileInputRef.value.click();
};

const handleDragOver = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const handleDragEnter = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const handleDragLeave = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const handleDrop = (event) => {
  event.preventDefault();
  event.stopPropagation();

  const files = event.dataTransfer.files;
  if (files.length > 0) {
    const file = files[0];
    // Simulate file input change event
    const fakeEvent = {
      target: {
        files: [file],
      },
    };
    handleFileSelect(fakeEvent);
  }
};

const removeFile = () => {
  mediaFile.value = null;
  mediaName.value = "";
  previewUrl.value = null;
  fileSize.value = 0;
  duration.value = 0;
  mediaUrl.value = "";
  fileDimensions.value = "";
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const saveMedia = () => {
  if (!mediaFile.value && !mediaUrl.value) {
    toast.error("Please select a file or provide a URL");
    return;
  }

  if (!mediaName.value) {
    toast.error("Please enter a media name");
    return;
  }

  isSubmitted.value = true;
  isUploading.value = true;
  uploadPercentage.value = 0;

  let data = new FormData();
  data.append("name", mediaName.value);
  data.append("media_file", mediaFile.value ? mediaFile.value : "");
  data.append("media_url", mediaUrl.value);
  data.append("media_type", mediaType.value);
  data.append("media_size", fileSize.value);
  data.append("media_dimensions", fileDimensions.value);
  data.append("media_input_mode", inputMode.value);
  data.append("media_duration", duration.value);

  axios
    .post(
      `${process.env.VUE_APP_BASE_URL}/api/media-library/${company_id}`,
      data,
      {
        headers: headers,
        withCredentials: true,
        onUploadProgress: function (progressEvent) {
          uploadPercentage.value = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      }
    )
    .then((res) => {
      toast.success(res.data.message);
      isSubmitted.value = false;
      isUploading.value = false;
      router.push("/media-library");
    })
    .catch((e) => {
      toast.error(e.response.data.message);
      isSubmitted.value = false;
      isUploading.value = false;
    });
};

const cancel = () => {
  if (isUploading.value) {
    const confirmLeave = confirm(
      "Upload is in progress. Are you sure you want to leave?"
    );
    if (!confirmLeave) return;
  }
  router.push("/media-library");
};

// Warn user before leaving page during upload
const handleBeforeUnload = (e) => {
  if (isUploading.value) {
    e.preventDefault();
    e.returnValue = "";
    return "";
  }
};

// Add event listener on mount
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", handleBeforeUnload);
}

// Remove event listener on unmount
onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  }
});

// Vue Router navigation guard - prevent navigation during upload
onBeforeRouteLeave((to, from, next) => {
  if (isUploading.value) {
    const confirmLeave = confirm(
      "Upload is in progress. Are you sure you want to leave? Your upload will be cancelled."
    );
    if (confirmLeave) {
      next();
    } else {
      next(false);
    }
  } else {
    next();
  }
});
</script>

<template>
  <AgentLayout>
    <PageHeader
      title="Upload New Media"
      subtitle="Add new images, videos, or documents to your media library"
      :showButton="false"
    />

    <div class="create-media-container">
      <div class="media-editor-layout">
        <!-- Left Panel: Form (60%) -->
        <div class="form-panel card">
          <!-- Input Mode Toggle -->
          <div class="form-group">
            <label class="form-label">Input Method</label>
            <div class="mode-toggle">
              <button
                type="button"
                class="mode-btn"
                :class="{ active: inputMode === 'file' }"
                @click="switchInputMode('file')"
              >
                📁 Upload File
              </button>
              <button
                type="button"
                class="mode-btn"
                :class="{ active: inputMode === 'url' }"
                @click="switchInputMode('url')"
              >
                🔗 Media URL
              </button>
            </div>
          </div>

          <!-- File Upload Area (shown when inputMode is 'file') -->
          <div v-if="inputMode === 'file'" class="form-group">
            <label class="form-label">Media File *</label>
            <div
              class="upload-area"
              :class="{ 'has-file': mediaFile }"
              @click="triggerFileInput"
              @dragover="handleDragOver"
              @dragenter="handleDragEnter"
              @dragleave="handleDragLeave"
              @drop="handleDrop"
            >
              <input
                ref="fileInputRef"
                type="file"
                accept="image/*,video/*,.pdf,.doc,.docx"
                @change="handleFileSelect"
                style="display: none"
              />

              <div v-if="!mediaFile" class="upload-placeholder">
                <div class="upload-icon">📁</div>
                <p class="upload-text">Click to browse or drag and drop</p>
                <p class="upload-hint">
                  Supports: Images (JPG, PNG, GIF), Videos (MP4, MOV), Documents
                  (PDF, DOC)
                </p>
              </div>

              <div v-else class="file-preview-small">
                <div class="file-info-compact">
                  <span class="file-icon">{{
                    mediaType === "image"
                      ? "🖼️"
                      : mediaType === "video"
                      ? "🎥"
                      : "📄"
                  }}</span>
                  <span class="file-name">{{ mediaName }}</span>
                </div>
                <button
                  type="button"
                  class="remove-file-btn-small"
                  @click.stop="removeFile"
                  :disabled="isUploading"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>

          <!-- URL Input (shown when inputMode is 'url') -->
          <div v-else class="form-group">
            <DInput
              :value="mediaUrl"
              @input="(e) => { mediaUrl = e.target.value; handleUrlInput(); }"
              type="url"
              label="https://example.com/image.jpg"
              width="100%"
            />
            <p class="input-hint">
              Enter a direct URL to an image, video, or document
            </p>
          </div>

          <!-- Media Name -->
          <div class="form-group">
            <DInput
              :value="mediaName"
              @input="mediaName = $event.target.value"
              type="text"
              label="Media Name"
              width="100%"
              class="w-full"
            />
          </div>

          <!-- Media Type -->
          <div class="form-group">
            <label class="form-label" for="mediaType">Media Type</label>
            <select id="mediaType" v-model="mediaType" class="form-select">
              <option value="" selected>Select Media Type</option>
              <option
                v-for="type in fileTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.icon }} {{ type.label }}
              </option>
            </select>
          </div>

          <!-- File Info (Read-only) -->
          <div v-if="mediaFile" class="file-info-section">
            <h3 class="section-title">File Information</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">File Size:</span>
                <span class="info-value">{{ formattedSize }}</span>
              </div>
              <div v-if="fileDimensions" class="info-item">
                <span class="info-label">Dimensions:</span>
                <span class="info-value">{{ fileDimensions }}</span>
              </div>
              <div v-if="duration && mediaType === 'video'" class="info-item">
                <span class="info-label">Duration:</span>
                <span class="info-value">{{ formattedDuration }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Type:</span>
                <span class="info-value">{{ mediaFile.type }}</span>
              </div>
            </div>
          </div>

          <!-- Upload Progress Bar -->
          <div
            v-if="isUploading && inputMode === 'file'"
            class="upload-progress-section"
          >
            <div v-if="isSubmitted" class="mt-20 w-full bg-gray-200 rounded-full h-4">
              <div class="bg-blue-500 h-4 rounded-full transition-all flex items-center justify-center"
                   :style="{ width: uploadPercentage + '%' }">
                <span class="text-xs text-white px-2">{{ uploadPercentage }}%</span>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button
              :disabled="isSubmitted"
              type="button"
              class="btn btn-secondary"
              @click="cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveMedia"
              :disabled="
                (inputMode === 'file' && (!mediaFile || !mediaName)) ||
                (inputMode === 'url' && (!mediaUrl || !mediaName)) ||
                !mediaType ||
                isSubmitted
              "
            >
              <i class="bi bi-upload"></i>
              Upload Media
            </button>
          </div>
        </div>

        <!-- Right Panel: Preview (40%) -->
        <div class="preview-panel card">
          <div class="preview-header">
            <h3>Preview</h3>
          </div>
          <div class="preview-content">
            <div v-if="!mediaFile" class="preview-empty">
              <div class="empty-icon">👁️</div>
              <p>No media selected</p>
              <p class="empty-hint">Upload a file to see preview</p>
            </div>

            <div v-else class="preview-display">
              <div
                v-if="mediaType === 'image' && previewUrl"
                class="preview-image"
              >
                <img :src="previewUrl" alt="Preview" />
              </div>
              <div
                v-else-if="mediaType === 'video' && previewUrl"
                class="preview-video"
              >
                <video :src="previewUrl" controls></video>
              </div>
              <div v-else class="preview-document">
                <div class="document-icon">📄</div>
                <p class="document-name">{{ mediaName }}</p>
                <p class="document-type">{{ mediaFile.type }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<style scoped>
.create-media-container {
  padding: 0;
}

.form-group :deep(.d-input-wrapper),
.form-group :deep(.d-input-wrapper .p-float-label),
.form-group :deep(.d-input-wrapper .p-inputtext) {
  width: 100% !important;
}

.media-editor-layout {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 24px;
  align-items: start;
}

@media (max-width: 1024px) {
  .media-editor-layout {
    grid-template-columns: 1fr;
  }
}

.form-panel,
.preview-panel {
  padding: 32px;
  background: white;
  border-radius: 8px;
  height: fit-content;
}

.preview-panel {
  position: sticky;
  top: 24px;
}

.preview-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.preview-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.preview-content {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-empty {
  text-align: center;
  color: #64748b;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.preview-empty p {
  margin: 8px 0;
  font-size: 16px;
  font-weight: 500;
}

.empty-hint {
  font-size: 14px !important;
  color: #94a3b8 !important;
}

.preview-display {
  width: 100%;
}

.preview-image img,
.preview-video video {
  max-width: 100%;
  max-height: 500px;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.preview-document {
  text-align: center;
  padding: 40px;
  background: #f8fafc;
  border-radius: 8px;
}

.document-icon {
  font-size: 80px;
  margin-bottom: 16px;
}

.document-name {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 8px 0;
  word-break: break-word;
}

.document-type {
  font-size: 14px;
  color: #64748b;
  margin: 4px 0;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #0f172a;
  margin-bottom: 8px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  background: white;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(47, 77, 132, 0.1);
}

.form-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
}

.input-hint {
  margin: 8px 0 0 0;
  font-size: 13px;
  color: #64748b;
}

/* Mode Toggle */
.mode-toggle {
  display: flex;
  gap: 8px;
  background: #f8fafc;
  padding: 4px;
  border-radius: 8px;
}

.mode-btn {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.mode-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.mode-btn.active {
  background: white;
  color: var(--primary-color);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Upload Area */
.upload-area {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f8fafc;
  position: relative;
}

.upload-area:hover {
  border-color: var(--primary-color);
  background: #f1f5f9;
}

.upload-area.has-file {
  border-style: solid;
  border-color: var(--primary-color);
  padding: 16px;
  background: white;
}

.upload-placeholder {
  pointer-events: none;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.upload-text {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.upload-hint {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Compact File Preview in Upload Area */
.file-preview-small {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
}

.file-info-compact {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.file-icon {
  font-size: 32px;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-file-btn-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: background 0.2s;
  flex-shrink: 0;
}

.remove-file-btn-small:hover {
  background: rgba(220, 38, 38, 1);
}

/* File Info Section */
.file-info-section {
  margin-top: 24px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 16px 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  color: #0f172a;
  font-weight: 500;
}

/* Upload Progress Section */
.upload-progress-section {
  margin-top: 24px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.progress-percentage {
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-color);
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-hover) 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .form-panel,
[data-theme="dark"] .preview-panel {
  background: #1e2a3e;
  border-color: #2d3f5a;
}

[data-theme="dark"] .preview-header {
  border-bottom-color: #2d3f5a;
}

[data-theme="dark"] .preview-header h3 {
  color: #e8edf5;
}

[data-theme="dark"] .form-label {
  color: #e8edf5;
}

[data-theme="dark"] .form-input,
[data-theme="dark"] .form-select {
  background: #28364e;
  border-color: #3d5070;
  color: #e8edf5;
}

[data-theme="dark"] .form-input::placeholder {
  color: #6480a0;
}

[data-theme="dark"] .form-input:focus,
[data-theme="dark"] .form-select:focus {
  border-color: #4e7fff;
  box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.15);
}

[data-theme="dark"] .form-select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239aadc4' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
}

[data-theme="dark"] .form-select option {
  background: #28364e;
  color: #e8edf5;
}

[data-theme="dark"] .input-hint {
  color: #6480a0;
}

[data-theme="dark"] .mode-toggle {
  background: #161c2c;
}

[data-theme="dark"] .mode-btn {
  color: #9aadc4;
}

[data-theme="dark"] .mode-btn:hover {
  background: #2d3f5a;
  color: #e8edf5;
}

[data-theme="dark"] .mode-btn.active {
  background: #28364e;
  color: #4e7fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

[data-theme="dark"] .upload-area {
  border-color: #3d5070;
  background: #161c2c;
}

[data-theme="dark"] .upload-area:hover {
  border-color: #4e7fff;
  background: #1e2a3e;
}

[data-theme="dark"] .upload-area.has-file {
  border-color: #4e7fff;
  background: #1e2a3e;
}

[data-theme="dark"] .upload-text {
  color: #e8edf5;
}

[data-theme="dark"] .upload-hint {
  color: #6480a0;
}

[data-theme="dark"] .file-preview-small {
  background: #28364e;
}

[data-theme="dark"] .file-name {
  color: #e8edf5;
}

[data-theme="dark"] .file-info-section {
  background: #161c2c;
  border-color: #2d3f5a;
}

[data-theme="dark"] .section-title {
  color: #e8edf5;
}

[data-theme="dark"] .info-label {
  color: #6480a0;
}

[data-theme="dark"] .info-value {
  color: #e8edf5;
}

[data-theme="dark"] .upload-progress-section {
  background: #161c2c;
  border-color: #2d3f5a;
}

[data-theme="dark"] .action-buttons {
  border-top-color: #2d3f5a;
}

[data-theme="dark"] .btn-secondary {
  background: #28364e;
  color: #9aadc4;
  border-color: #3d5070;
}

[data-theme="dark"] .btn-secondary:hover {
  background: #3d5070;
  border-color: #4e7fff;
  color: #e8edf5;
}

[data-theme="dark"] .preview-empty {
  color: #9aadc4;
}

[data-theme="dark"] .empty-hint {
  color: #6480a0 !important;
}

[data-theme="dark"] .preview-document {
  background: #161c2c;
}

[data-theme="dark"] .document-name {
  color: #e8edf5;
}

[data-theme="dark"] .document-type {
  color: #6480a0;
}
</style>
